import React, { useEffect, useState, useMemo } from "react";
import { Plus, Pencil, Trash2, Shield } from "lucide-react";
import {
  Badge,
  Button,
  Checkbox,
  Field,
  Input,
  ListPage,
  Modal,
  Select,
  Textarea,
} from "@redipos/ui";
import type {
  ConsolePlanApi,
  ConsolePlanFeatureCatalogGroup,
  ConsolePlanFeatureCatalogResponse,
  PlanFeatureDefinition,
} from "@redipos/contracts";
import { finanzasApi } from "../api.js";

function errMsg(e: unknown, fallback = "Error") {
  return e instanceof Error ? e.message : fallback;
}

const emptyForm = () => ({
  name: "",
  description: "",
  price: 0,
  currency: "PYG",
  interval: "monthly",
  sortOrder: 0,
  trialDays: 0,
  maxUsers: 2,
  maxProducts: 200,
  maxStorageMb: 500,
  supportLevel: "basic",
  recommended: false,
  active: true,
  visibleEnWeb: true,
  featureFlags: {} as Record<string, boolean>,
  marketingBullets: "",
});

export default function PlansPage() {
  const [catalog, setCatalog] = useState<ConsolePlanFeatureCatalogResponse | null>(null);
  const [plans, setPlans] = useState<ConsolePlanApi[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<"create" | string | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [cat, pls] = await Promise.all([
        finanzasApi.planFeatureCatalog(),
        finanzasApi.plans(),
      ]);
      setCatalog(cat);
      setPlans(pls);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const definitionsByGroup = useMemo(() => {
    if (!catalog?.definitions) return {};
    const m: Record<string, PlanFeatureDefinition[]> = {};
    for (const d of catalog.definitions) {
      if (!m[d.group]) m[d.group] = [];
      m[d.group].push(d);
    }
    return m;
  }, [catalog]);

  const openCreate = () => {
    setForm(emptyForm());
    setModal("create");
    setErr(null);
  };

  const openEdit = (plan: ConsolePlanApi) => {
    const fv = plan.featuresView || { version: 2, flags: {}, marketingBullets: [] };
    const flags = fv.flags || {};
    setForm({
      name: plan.name,
      description: plan.description || "",
      price: parseFloat(plan.price) || 0,
      currency: plan.currency || "PYG",
      interval: plan.interval || "monthly",
      sortOrder: plan.sortOrder ?? 0,
      trialDays: plan.trialDays ?? 0,
      maxUsers: plan.maxUsers ?? 1,
      maxProducts: plan.maxProducts ?? 50,
      maxStorageMb: plan.maxStorageMb ?? 100,
      supportLevel: plan.supportLevel || "basic",
      recommended: !!plan.recommended,
      active: plan.active !== false,
      visibleEnWeb: plan.visibleEnWeb !== false,
      featureFlags: { ...flags },
      marketingBullets: (fv.marketingBullets || []).join("\n"),
    });
    setModal(plan.id);
    setErr(null);
  };

  const toggleFlag = (key: string) => {
    setForm((f) => ({
      ...f,
      featureFlags: { ...f.featureFlags, [key]: !f.featureFlags[key] },
    }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    const bullets = form.marketingBullets
      .split("\n")
      .map((x) => x.trim())
      .filter(Boolean);
    const body = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      price: form.price,
      currency: form.currency,
      interval: form.interval,
      sortOrder: form.sortOrder,
      trialDays: form.trialDays,
      maxUsers: form.maxUsers,
      maxProducts: form.maxProducts,
      maxStorageMb: form.maxStorageMb,
      supportLevel: form.supportLevel,
      recommended: form.recommended,
      active: form.active,
      visibleEnWeb: form.visibleEnWeb,
      featureFlags: form.featureFlags,
      marketingBullets: bullets,
    };
    try {
      if (modal === "create") {
        await finanzasApi.createPlan(body);
      } else {
        await finanzasApi.updatePlan(String(modal), body);
      }
      setModal(null);
      await load();
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (plan: ConsolePlanApi) => {
    if (
      !confirm(
        `¿Eliminar el plan «${plan.name}»? No debe tener suscripciones activas.`,
      )
    )
      return;
    setDeleting(plan.id);
    try {
      await finanzasApi.deletePlan(plan.id);
      await load();
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setDeleting(null);
    }
  };

  const groupOrder: ConsolePlanFeatureCatalogGroup[] =
    catalog?.groups?.slice().sort((a, b) => a.order - b.order) || [];
  const recommendedCount = plans.filter((plan) => plan.recommended).length;
  const activeCount = plans.filter((plan) => plan.active !== false).length;
  const metrics = [
    { key: "total", label: "Total planes", value: loading ? "—" : plans.length },
    { key: "active", label: "Activos", value: loading ? "—" : activeCount },
    { key: "recommended", label: "Recomendados", value: loading ? "—" : recommendedCount },
  ];

  return (
    <>
      <ListPage
        title="Planes de suscripción"
        subtitle="Límites y módulos habilitados por plan"
        sectionTitle="Listado de planes"
        metrics={metrics}
        actions={
          <Button type="button" onClick={openCreate}>
            <Plus className="w-4 h-4" />
            Nuevo plan
          </Button>
        }
      >
        {err && !modal && <p className="text-sm text-danger">{err}</p>}
        {loading ? (
          <p className="text-fg-subtle text-sm">Cargando…</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {plans.map((plan) => {
              const fv = plan.featuresView || { flags: {}, marketingBullets: [] };
              const activeFlags = Object.entries(fv.flags || {}).filter(
                ([, v]) => v,
              ).length;
              return (
                <div
                  key={plan.id}
                  className={`rounded-card border p-5 flex flex-col shadow-card transition-colors ${
                    plan.recommended
                      ? "border-brand-500/40 bg-brand-500/5"
                      : "border-border bg-surface/40"
                  }`}
                >
                  <div className="flex justify-between items-start gap-2 mb-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <Shield className="w-5 h-5 text-fg-subtle shrink-0" />
                      <h2 className="font-semibold text-fg truncate">
                        {plan.name}
                      </h2>
                      {plan.recommended && (
                        <Badge variant="brand" size="sm">
                          Reco
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => openEdit(plan)}
                        className="p-2 rounded-input text-fg-subtle hover:bg-surface-elevated hover:text-fg transition-colors"
                        title="Editar"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(plan)}
                        disabled={deleting === plan.id}
                        className="p-2 rounded-input text-fg-subtle hover:bg-danger-soft hover:text-danger transition-colors disabled:opacity-50"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-fg-subtle line-clamp-2 flex-1">
                    {plan.description || "—"}
                  </p>
                  <p className="text-lg font-semibold text-fg mt-3 tabular-nums">
                    {plan.currency} {Number(plan.price).toLocaleString("es-PY")}
                    <span className="text-xs font-normal text-fg-subtle">
                      {" "}
                      / {plan.interval === "yearly" ? "año" : "mes"}
                    </span>
                  </p>
                  <p className="text-[11px] text-fg-subtle mt-2">
                    {activeFlags} módulos · {plan.maxUsers} usuarios ·{" "}
                    {plan.maxProducts} productos
                  </p>
                  {!plan.active && (
                    <div className="mt-3">
                      <Badge variant="warning" size="sm">
                        Inactivo
                      </Badge>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </ListPage>

      <Modal
        open={!!modal}
        onClose={() => setModal(null)}
        title={modal === "create" ? "Nuevo plan" : "Editar plan"}
        size="lg"
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setModal(null)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              form="plan-form"
              loading={saving}
              loadingLabel="Guardando…"
            >
              Guardar
            </Button>
          </>
        }
      >
        <form id="plan-form" onSubmit={submit} className="space-y-5">
          {err && <p className="text-sm text-danger">{err}</p>}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Field label="Nombre" required>
                <Input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Descripción">
                <Textarea
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  rows={2}
                />
              </Field>
            </div>
            <Field label="Precio" required>
              <Input
                type="number"
                step="any"
                required
                value={form.price}
                onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
              />
            </Field>
            <Field label="Moneda">
              <Select
                value={form.currency}
                onChange={(e) =>
                  setForm({ ...form, currency: e.target.value })
                }
              >
                <option value="PYG">PYG</option>
                <option value="USD">USD</option>
              </Select>
            </Field>
            <Field label="Intervalo">
              <Select
                value={form.interval}
                onChange={(e) =>
                  setForm({ ...form, interval: e.target.value })
                }
              >
                <option value="monthly">Mensual</option>
                <option value="yearly">Anual</option>
              </Select>
            </Field>
            <Field label="Orden">
              <Input
                type="number"
                value={form.sortOrder}
                onChange={(e) =>
                  setForm({ ...form, sortOrder: Number(e.target.value) })
                }
              />
            </Field>
            <Field label="Días de prueba">
              <Input
                type="number"
                value={form.trialDays}
                onChange={(e) =>
                  setForm({ ...form, trialDays: Number(e.target.value) })
                }
              />
            </Field>
            <Field label="Máx. usuarios">
              <Input
                type="number"
                value={form.maxUsers}
                onChange={(e) =>
                  setForm({ ...form, maxUsers: Number(e.target.value) })
                }
              />
            </Field>
            <Field label="Máx. productos">
              <Input
                type="number"
                value={form.maxProducts}
                onChange={(e) =>
                  setForm({ ...form, maxProducts: Number(e.target.value) })
                }
              />
            </Field>
            <Field label="Almacenamiento MB">
              <Input
                type="number"
                value={form.maxStorageMb}
                onChange={(e) =>
                  setForm({ ...form, maxStorageMb: Number(e.target.value) })
                }
              />
            </Field>
            <Field label="Soporte">
              <Select
                value={form.supportLevel}
                onChange={(e) =>
                  setForm({ ...form, supportLevel: e.target.value })
                }
              >
                <option value="basic">Básico</option>
                <option value="priority">Prioritario</option>
                <option value="dedicated">Dedicado</option>
              </Select>
            </Field>
          </div>

          <div className="flex flex-wrap gap-4 border-t border-border pt-4">
            <Checkbox
              checked={form.recommended}
              onChange={(e) =>
                setForm({ ...form, recommended: e.target.checked })
              }
              label="Destacar como recomendado"
            />
            <Checkbox
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              label="Plan activo"
            />
            <Checkbox
              checked={form.visibleEnWeb}
              onChange={(e) =>
                setForm({ ...form, visibleEnWeb: e.target.checked })
              }
              label="Visible en web pública (precios)"
            />
          </div>

          <div className="space-y-3 border-t border-border pt-4">
            <p className="text-sm font-medium text-fg-muted">
              Módulos y funciones
            </p>
            {groupOrder.map((g) => (
              <div key={g.id} className="space-y-2">
                <p className="text-[11px] uppercase tracking-wide text-fg-subtle">
                  {g.title}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {(definitionsByGroup[g.id] || []).map((def) => (
                    <label
                      key={def.key}
                      className="flex items-start gap-3 p-3 rounded-input border border-border bg-surface-sunken/60 cursor-pointer hover:border-border-strong transition-colors"
                    >
                      <input
                        type="checkbox"
                        className="mt-1 rounded border-border-strong accent-brand-600"
                        checked={!!form.featureFlags[def.key]}
                        onChange={() => toggleFlag(def.key)}
                      />
                      <span>
                        <span className="block text-sm text-fg">
                          {def.label}
                        </span>
                        <span className="block text-[11px] text-fg-subtle">
                          {def.description}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <Field
            label="Textos de marketing (uno por línea; para landing / comparador)"
          >
            <Textarea
              value={form.marketingBullets}
              onChange={(e) =>
                setForm({ ...form, marketingBullets: e.target.value })
              }
              rows={4}
              placeholder={"Ej: Soporte por chat\nSin tarjeta en plan gratuito"}
            />
          </Field>
        </form>
      </Modal>
    </>
  );
}
